import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { IMAGES } from "../constants";
import { useLang } from "../i18n";

const reveal = {
  hidden: { y: "112%" },
  show: (i) => ({ y: "0%", transition: { delay: 0.15 + i * 0.15, duration: 1.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero({ start = true }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const mx = useSpring(0, { stiffness: 60, damping: 18 });
  const my = useSpring(0, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(my, [-0.5, 0.5], [2.4, -2.4]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-2.8, 2.8]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const appear = (delay) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: start ? 1 : 0, y: start ? 0 : 18 },
    transition: { delay: start ? delay : 0, duration: 0.9 },
  });

  return (
    <section id="top" ref={ref} onMouseMove={onMouseMove} className="relative h-screen overflow-hidden" data-testid="hero">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src={IMAGES.hero}
          alt=""
          className="h-full w-full object-cover"
          initial={{ scale: 1.14 }}
          animate={{ scale: start ? 1.02 : 1.14 }}
          transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-obsidian/45" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_110%,#0A0808_10%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_80%_0%,rgba(139,0,0,0.35),transparent_55%)]" />
      </motion.div>

      <motion.div style={{ opacity }} className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 pb-10 pt-24 text-center">
        <motion.div style={{ rotateX, rotateY, transformPerspective: 1000 }} className="flex flex-col items-center">
          <motion.p
            variants={reveal}
            custom={0}
            initial="hidden"
            animate={start ? "show" : "hidden"}
            className="mb-8 overflow-hidden text-[0.6rem] tracking-[0.45em] text-gold/90"
          >
            <span className="block">{t("hero.eyebrow")}</span>
          </motion.p>

          <h1 className="font-display leading-[0.95]">
            <span className="block overflow-hidden">
              <motion.span
                variants={reveal}
                custom={2}
                initial="hidden"
                animate={start ? "show" : "hidden"}
                className="text-logo-gold block text-[12.5vw] font-black tracking-[0.1em] md:text-[7.2vw]"
              >
                {t("hero.line1")} {t("hero.line2")}
              </motion.span>
            </span>
          </h1>

          <div className="overflow-hidden">
            <motion.p
              variants={reveal}
              custom={3}
              initial="hidden"
              animate={start ? "show" : "hidden"}
              className="text-logo-gold font-display mt-7 text-[1rem] tracking-[0.5em] md:text-2xl"
            >
              {t("hero.tagline")}
            </motion.p>
          </div>
        </motion.div>

        <motion.p {...appear(0.55)} className="mt-8 max-w-xl text-sm leading-relaxed text-parchment/80 md:text-lg">
          {t("hero.sub")}
        </motion.p>

        <motion.div {...appear(0.75)} className="mt-9 flex flex-wrap justify-center gap-4">
          <a href="#collection" data-testid="hero-cta-collection" className="btn-gold">
            {t("hero.cta1")}
          </a>
          <a href="#wholesale" data-testid="hero-cta-wholesale" className="btn-outline-gold">
            {t("hero.cta2")}
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 start-1/2 z-10 hidden -translate-x-1/2 md:block" data-testid="hero-scroll-indicator">
        <motion.div {...appear(1)} className="flex flex-col items-center gap-3">
          <span className="text-[0.6rem] uppercase tracking-[0.4em] text-parchment/50">{t("hero.scroll")}</span>
          <div className="h-16 w-px overflow-hidden bg-gold/15">
            <div className="scroll-thread h-full w-full bg-gold" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
